import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Play, Clock, CheckCircle2 } from "lucide-react";
import Seo from "../components/Seo";
import { STORY_COLLECTIONS } from "../data/regalStories";

export default function Stories() {
  const played = useMemo(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("rj_stories_played") || "[]"));
    } catch (e) {
      console.debug("played list read failed:", e?.message);
      return new Set();
    }
  }, []);
  const total = STORY_COLLECTIONS.reduce((a, c) => a + c.stories.length, 0);
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14" data-testid="stories-page">
      <Seo
        title="Regal Stories — Narrated by Mr. Regal"
        description="Short narrated stories about gold, family, tradition and jewelry wisdom — voiced by Mr. Regal of Regal Jewels, Chicago. History, emotion, education and guidance in 30 to 60 seconds each."
        path="/stories"
      />
      <div className="lg:flex lg:items-start lg:gap-10">
        <div className="flex-1">
          <p className="text-[11px] tracking-[0.4em] uppercase text-[#a8842c]">Narrated by Mr. Regal</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-[#171310] mt-3" data-testid="stories-title">Regal Stories</h1>
          <p className="text-[#5c5240] mt-4 max-w-2xl leading-relaxed">
            Short stories of gold, family and wisdom — each 30 to 60 seconds, told in Mr. Regal's voice with full subtitles. Choose a story and press play.
          </p>
          {played.size > 0 && (
            <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#8a6a1f] mt-4" data-testid="stories-heard-count">
              <CheckCircle2 size={14} /> You've heard {played.size} of {total} stories
            </p>
          )}
        </div>
        <img src="/mr-regal-welcome.webp" alt="" aria-hidden="true" loading="lazy" decoding="async" className="hidden lg:block h-44 w-auto shrink-0 opacity-90" />
      </div>

      {STORY_COLLECTIONS.map((col) => (
        <section key={col.key} className="mt-14" data-testid={`stories-collection-${col.key}`}>
          <div className="flex items-baseline gap-4 flex-wrap">
            <h2 className="font-heading text-2xl sm:text-3xl text-[#171310]">{col.title}</h2>
            <p className="text-sm text-[#8f8571]">{col.blurb}</p>
          </div>
          <div className="h-px w-24 mt-3 bg-gradient-to-r from-[#c9a24b] to-transparent" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {col.stories.map((s, i) => (
              <Link
                key={s.slug}
                to={`/stories/${s.slug}`}
                className="group border border-[#e8e0d0] hover:border-[#a8842c] bg-white/70 p-5 transition-colors duration-300 flex flex-col"
                data-testid={`story-card-${s.slug}`}
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2.5">
                    <span className="font-heading text-sm text-[#c9a24b]">{String(i + 1).padStart(2, "0")}</span>
                    {played.has(s.slug) && (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase text-[#8a6a1f]" data-testid={`story-played-${s.slug}`}>
                        <CheckCircle2 size={12} /> Heard
                      </span>
                    )}
                  </span>
                  <span className="h-9 w-9 rounded-full border border-[#d8cdb5] group-hover:border-[#a8842c] group-hover:bg-[#a8842c]/10 flex items-center justify-center text-[#8a6a1f] transition-colors duration-300">
                    <Play size={13} className="ml-0.5" />
                  </span>
                </div>
                <h3 className="font-heading text-lg text-[#171310] leading-snug mt-3 group-hover:text-[#8a6a1f] transition-colors">{s.title}</h3>
                <p className="text-[13px] sm:text-xs text-[#8f8571] leading-relaxed mt-2 flex-1">{s.teaser}</p>
                <p className="inline-flex items-center gap-1.5 text-[11px] sm:text-[10px] tracking-[0.2em] uppercase text-[#b1a685] mt-4">
                  <Clock size={11} /> 30–60 sec
                </p>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
